import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Plus, Loader2 } from 'lucide-react';
import type { ResourceSeries } from '../../../hooks/useAdminSeries';

const BOOKS_OF_BIBLE = [
  'Genesis','Exodus','Leviticus','Numbers','Deuteronomy','Joshua','Judges','Ruth',
  '1 Samuel','2 Samuel','1 Kings','2 Kings','1 Chronicles','2 Chronicles','Ezra',
  'Nehemiah','Esther','Job','Psalms','Proverbs','Ecclesiastes','Song of Solomon',
  'Isaiah','Jeremiah','Lamentations','Ezekiel','Daniel','Hosea','Joel','Amos',
  'Obadiah','Jonah','Micah','Nahum','Habakkuk','Zephaniah','Haggai','Zechariah',
  'Malachi','Matthew','Mark','Luke','John','Acts','Romans','1 Corinthians',
  '2 Corinthians','Galatians','Ephesians','Philippians','Colossians',
  '1 Thessalonians','2 Thessalonians','1 Timothy','2 Timothy','Titus','Philemon',
  'Hebrews','James','1 Peter','2 Peter','1 John','2 John','3 John','Jude','Revelation',
  'Topical',
];

interface Props {
  series: ResourceSeries | null;
  onSave: (fields: Partial<ResourceSeries>) => Promise<ResourceSeries | void>;
  onUploadCoverArt: (seriesId: string, file: File) => Promise<void>;
  onClose: () => void;
}

export function SeriesEditorModal({ series, onSave, onUploadCoverArt, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [bookOfBible, setBookOfBible] = useState('');
  const [topic, setTopic] = useState('');
  const [weekCount, setWeekCount] = useState(1);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (series) {
      setTitle(series.title);
      setDescription(series.description ?? '');
      setBookOfBible(series.book_of_bible ?? '');
      setTopic(series.topic ?? '');
      setWeekCount(series.week_count);
      setTags(series.tags ?? []);
      setCoverPreview(series.cover_art_url ?? null);
    } else {
      setTitle(''); setDescription(''); setBookOfBible('');
      setTopic(''); setWeekCount(1); setTags([]);
      setCoverPreview(null);
    }
    setCoverFile(null);
  }, [series]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const fields: Partial<ResourceSeries> = {
        title: title.trim(),
        description: description.trim() || null,
        book_of_bible: bookOfBible || null,
        topic: topic.trim() || null,
        week_count: weekCount,
        tags,
      };
      const result = await onSave(fields);
      const savedId = series?.id ?? (result as ResourceSeries)?.id;
      if (coverFile && savedId) {
        await onUploadCoverArt(savedId, coverFile);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-charcoal rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-sand/20 dark:border-slate/20">
          <h2 className="text-base font-semibold text-midnight dark:text-soft-white">
            {series ? 'Edit Series' : 'New Series'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-sand/20 dark:hover:bg-slate/20">
            <X className="h-4 w-4 text-slate" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {error && (
            <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">{error}</p>
          )}

          {/* Cover art */}
          <div>
            <label className="block text-xs font-medium text-slate dark:text-soft-white/60 mb-1">Cover Art</label>
            <div className="flex items-center gap-3">
              {coverPreview
                ? <img src={coverPreview} alt="Cover" className="w-32 rounded object-cover" style={{ aspectRatio: '16/9' }} />
                : <div className="w-32 rounded bg-sand/30 dark:bg-slate/20 flex items-center justify-center" style={{ aspectRatio: '16/9' }}>
                    <Upload className="h-5 w-5 text-slate/40" />
                  </div>
              }
              <button type="button" onClick={() => fileRef.current?.click()}
                className="px-3 py-1.5 text-sm border border-sand/40 dark:border-slate/30 rounded-lg hover:bg-sand/10 text-midnight dark:text-soft-white">
                {coverPreview ? 'Change Image' : 'Upload Image'}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate dark:text-soft-white/60 mb-1">Title *</label>
            <input
              value={title} onChange={e => setTitle(e.target.value)} required
              className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white"
              placeholder="Series title"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate dark:text-soft-white/60 mb-1">Description</label>
            <textarea
              value={description} onChange={e => setDescription(e.target.value)} rows={3}
              className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white resize-none"
              placeholder="Brief description..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate dark:text-soft-white/60 mb-1">Book of Bible</label>
              <select
                value={bookOfBible} onChange={e => setBookOfBible(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white"
              >
                <option value="">— Select —</option>
                {BOOKS_OF_BIBLE.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate dark:text-soft-white/60 mb-1">Topic</label>
              <input
                value={topic} onChange={e => setTopic(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white"
                placeholder="e.g. Faith, Prayer..."
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate dark:text-soft-white/60 mb-1">Number of Weeks</label>
            <input
              type="number" min={1} max={52} value={weekCount} onChange={e => setWeekCount(Number(e.target.value))}
              className="w-24 px-3 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate dark:text-soft-white/60 mb-1">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                className="flex-1 px-3 py-1.5 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white"
                placeholder="Add tag, press Enter"
              />
              <button type="button" onClick={addTag}
                className="px-3 py-1.5 bg-electric text-white text-sm rounded-lg hover:bg-electric/90">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-sand/30 dark:bg-slate/20 rounded-full text-xs text-midnight dark:text-soft-white">
                    {t}
                    <button type="button" onClick={() => setTags(prev => prev.filter(x => x !== t))} className="hover:text-red-400">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </form>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-sand/20 dark:border-slate/20">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg hover:bg-sand/10 text-midnight dark:text-soft-white">
            Cancel
          </button>
          <button
            onClick={handleSubmit as unknown as React.MouseEventHandler}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-electric text-white text-sm rounded-lg hover:bg-electric/90 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {series ? 'Save Changes' : 'Create Series'}
          </button>
        </div>
      </div>
    </div>
  );
}
